import {Injectable, Injector} from '@angular/core';
import {BehaviorSubject, Observable, of, Subject} from 'rxjs';
import {distinctUntilChanged, filter, map, switchMap} from 'rxjs/operators';
import {
    WidgetImplementation,
    WidgetImplementationEvent,
    WidgetImplementationEventFlags
} from '../../components/layout/widget/widget-implementation';
import {SecurityService} from '../../components/security';
import {isSmartSecurityKey, SmartSecurityService} from '../../components/smart-security';
import {Security} from '../../models/security';
import {isSecurityKeysEqual, SecurityKey} from '../../models/security-key';
import {WidgetHeaderTitlePart} from '../../pages/dashboard/layout/widget/widget-header/widget-header-title';
import {
    getWidgetSettingsWithCorrectSecurityKey,
    getWidgetSettingsWithUpdatesStream,
    getWidgetWithAltSecurityTitlePartsStream,
    listenToWidgetSettingsSecurityInvalidation,
    pipeSettingsCorrections,
    setWidgetSettings,
    updateWidgetSettings
} from '../components/common-widget.implementation';
import {ChartWidgetSettings} from './settings/chart-widget-settings';
import {ChartWidgetSettingsService} from './settings/chart-widget-settings.service';

@Injectable()
export class ChartWidgetImplementation implements WidgetImplementation {

    public settings: ChartWidgetSettings;

    public settingsService: ChartWidgetSettingsService;

    public ready$ = new BehaviorSubject<boolean>(false);

    public event$ = new Subject<WidgetImplementationEvent>();

    public readonly security$: Observable<Security>;

    public settingsWithUpdates$: Observable<Partial<ChartWidgetSettings>> =
        getWidgetSettingsWithUpdatesStream(this);

    public readonly titleParts$: Observable<WidgetHeaderTitlePart[]>;

    // [HED-930] It's not SmartSecurityService here, since it requires accounts
    public readonly securityService: SecurityService = this.injector.get(SecurityService);

    constructor(
        public injector: Injector
    ) {
        this.titleParts$ = this.getTitlePartsStream();
        this.settingsService = this.injector.get(ChartWidgetSettingsService);
        this.security$ = this.getSecurityStream();

        listenToWidgetSettingsSecurityInvalidation(this);
    }

    public setSettings(
        settings?: ChartWidgetSettings,
        flags?: WidgetImplementationEventFlags
    ): Observable<ChartWidgetSettings> {
        return setWidgetSettings(this, settings, flags);
    }

    public updateSettings(
        updates?: Partial<ChartWidgetSettings>,
        flags?: WidgetImplementationEventFlags
    ): Observable<Partial<ChartWidgetSettings>> {
        return updateWidgetSettings(this, updates, flags);
    }

    public resolveSettings(
        settings: Partial<ChartWidgetSettings>
    ): Observable<Partial<ChartWidgetSettings>> {
        return pipeSettingsCorrections([
            s => getWidgetSettingsWithCorrectSecurityKey(this, s)
        ])(settings, this);
    }

    public getSettingsSecurityKeyStream(): Observable<SecurityKey> {
        return this.settingsWithUpdates$.pipe(
            map(settingsUpdates => settingsUpdates.securityKey),
            filter(securityKey => !!securityKey),
            distinctUntilChanged(isSecurityKeysEqual)
        );
    }

    private getSecurityStream(): Observable<Security> {
        return this.getSettingsSecurityKeyStream().pipe(
            switchMap(securityKey => this.securityService.getSecurityBySecurityKey(securityKey))
        );
    }

    private getTitlePartsStream(): Observable<WidgetHeaderTitlePart[]> {

        // [TT-247] Handle Smart security (workaround)
        const smartAltSecurityKeyResolver = (securityKey: SecurityKey): Observable<SecurityKey> =>
            securityKey && isSmartSecurityKey(securityKey)
                ? (this.securityService as SmartSecurityService).resolveSmartSecurity(securityKey).pipe(
                    map(securities => securities && securities[0])
                )
                : of(undefined);

        return getWidgetWithAltSecurityTitlePartsStream(this, smartAltSecurityKeyResolver);
    }

}
